import React, { useState, useEffect } from "react";
import {
  Phone,
  User,
  ShieldAlert,
  Heart,
  MapPin,
  Loader2,
  AlertCircle,
} from "lucide-react";
import {
  collectionGroup,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { calcAge } from "../utils/helpers";
import { translations } from "../utils/dictionary";

export default function PublicProfile({ tagId }: { tagId: string }) {
  const t = translations["uk"];

  const [petData, setPetData] = useState<any | null>(null);
  const [ownerData, setOwnerData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPetAndOwner = async () => {
      try {
        // 🚀 1. ШУКАЄМО ПЕСИКА МИТТЄВО ЧЕРЕЗ COLLECTION GROUP
        const petsQuery = query(
          collectionGroup(db, "pets"),
          where("id", "==", tagId)
        );
        const petSnapshot = await getDocs(petsQuery);

        if (!petSnapshot.empty) {
          const petDoc = petSnapshot.docs[0];
          const foundPet = petDoc.data();

          let foundOwner = null;

          // 🚀 2. ЯКЩО ПЕСИКА ЗНАЙДЕНО, БЕРЕМО ЙОГО ВЛАСНИКА ЗА 1 ЗАПИТ
          if (foundPet.userId) {
            const ownerSnap = await getDoc(doc(db, "users", foundPet.userId));
            if (ownerSnap.exists()) {
              foundOwner = ownerSnap.data();
            }
          }

          setPetData(foundPet);
          setOwnerData(foundOwner);
        } else {
          setError("not_found");
        }
      } catch (err: any) {
        console.error("🔥 Помилка Firebase:", err);
        // Спеціальний обробник для створення Індексу
        if (err.message && err.message.includes("index")) {
          setError("needs_index");
        } else {
          setError("error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPetAndOwner();
  }, [tagId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-main)] text-[#a073e7]">
        <Loader2 size={48} className="animate-spin mb-4" />
        <p className="font-bold">Шукаємо хвостика...</p>
      </div>
    );
  }

  // 🚀 НОВИЙ ЕКРАН ПІДКАЗКИ ДЛЯ ТЕБЕ (ЯК ДЛЯ РОЗРОБНИКА)
  if (error === "needs_index") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-main)] p-6 text-center">
        <AlertCircle size={64} className="text-[#f59e0b] mb-4" />
        <h2 className="text-2xl font-black text-[var(--text-main)] mb-2">
          Увага, Розробнику!
        </h2>
        <p className="text-[var(--text-muted)] font-medium mb-4">
          Для цього надшвидкого запиту Firebase потребує Індекс. Відкрий свій
          додаток на <b>комп'ютері</b>, натисни <b>F12 (Консоль)</b>, знайди там
          посилання від Firebase і клікни по ньому!
        </p>
      </div>
    );
  }

  if (error || !petData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-main)] p-6 text-center">
        <AlertCircle size={64} className="text-[var(--red-text)] mb-4" />
        <h2 className="text-2xl font-black text-[var(--text-main)] mb-2">
          Упс!
        </h2>
        <p className="text-[var(--text-muted)] font-medium">
          Песика з таким ID не знайдено або адреса невірна.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] animate-fade-in flex flex-col">
      {/* ШАПКА */}
      <div className="bg-gradient-to-br from-[#a073e7] to-[#b991f8] px-6 pt-12 pb-8 rounded-b-[2.5rem] shadow-md relative overflow-hidden flex flex-col items-center text-white">
        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-xl bg-purple-100 flex items-center justify-center text-4xl font-black text-[#a073e7] z-10 mb-4">
          {petData.photo ? (
            <img src={petData.photo} className="w-full h-full object-cover" />
          ) : (
            petData.name?.[0]
          )}
        </div>
        <h1 className="text-3xl font-black z-10 text-center">{petData.name}</h1>
        <p className="opacity-90 font-bold tracking-wide z-10 mt-1 flex items-center gap-1">
          {petData.breed || "Порода не вказана"} •{" "}
          {petData.gender || "Стать не вказана"}
        </p>
      </div>

      {/* ІНФОРМАЦІЯ ПРО ТВАРИНУ */}
      <div className="p-5 flex flex-col gap-4 -mt-4 relative z-20">
        <div className="bg-[var(--bg-card)] rounded-3xl p-5 shadow-sm border border-[var(--border-color)]">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                Вік
              </p>
              <p className="font-bold text-[15px]">
                {petData.dob ? calcAge(petData.dob, t) : "Невідомо"}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                Вага
              </p>
              <p className="font-bold text-[15px]">{petData.weight || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                Місто
              </p>
              <p className="font-bold text-[15px] flex items-center gap-1">
                <MapPin size={14} className="text-[#a073e7]" />
                {petData.city || "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                Номер Чіпу
              </p>
              <p className="font-bold text-[15px]">{petData.chip || "—"}</p>
            </div>
          </div>

          {petData.features && (
            <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-2 flex items-center gap-1">
                <Heart size={12} className="text-[var(--red-text)]" />{" "}
                Особливості / Медичні дані
              </p>
              <p className="text-sm font-medium whitespace-pre-wrap">
                {petData.features}
              </p>
            </div>
          )}
        </div>

        {/* КОНТАКТИ ВЛАСНИКА */}
        <div className="bg-[var(--bg-card)] rounded-3xl p-5 shadow-sm border-2 border-[#a073e7]">
          <h3 className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5 mb-4 text-[#a073e7]">
            <User size={16} /> Контакти власника
          </h3>
          <p className="font-bold text-lg mb-4">
            {ownerData?.ownerName || "Власник"}
          </p>

          {ownerData?.ownerPhone ? (
            <a
              href={`tel:${ownerData.ownerPhone}`}
              className="w-full py-4 bg-[#10b981] text-white font-black rounded-xl flex justify-center items-center gap-2 shadow-lg active:scale-95 transition-all text-base animate-bounce"
            >
              <Phone size={20} /> ЗАТЕЛЕФОНУВАТИ
            </a>
          ) : (
            <div className="p-4 bg-[var(--red-soft)] text-[var(--red-text)] rounded-xl text-sm font-bold flex items-center gap-2">
              <ShieldAlert size={20} /> Власник не залишив номер телефону
            </div>
          )}
        </div>
      </div>

      <div className="text-center py-6 text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest mt-auto">
        Powered by MyPaws
      </div>
    </div>
  );
}
